import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions, auth } from "../../../firebase.js";
import { Link, useNavigate } from "react-router-dom";
import "./createUser.css";

// Seasonal rate keys matching admin alteration buttons (Jacket, Pants, Shirt) – single rate per job
const SEASONAL_JACKET_KEYS = [
    { name: 'sleeveLonger', label: 'Sleeve Longer' },
    { name: 'sleeveShorter', label: 'Sleeve Shorter' },
    { name: 'takeInJacket', label: 'Take In' },
    { name: 'takeOutJacket', label: 'Take Out' },
    { name: 'shorterJacket', label: 'Shorter Jacket' },
    { name: 'shoulderPads', label: 'Shoulder Pads' },
    { name: 'functionButtonHole', label: 'Functional Button Hole' },
    { name: 'changeButtonsJacket', label: 'Change Buttons' },
    { name: 'taperSleeve', label: 'Taper Sleeve' },
    { name: 'shatnezTest', label: 'Shattnez Test' },
    { name: 'removeShattnez', label: 'Remove Shattnez' }
];
const SEASONAL_PANTS_KEYS = [
    { name: 'hemming', label: 'Hemming' },
    { name: 'taperPants', label: 'Taper Pants' },
    { name: 'waistIn', label: 'Waist In' },
    { name: 'waistOut', label: 'Waist Out' },
    { name: 'seatIn', label: 'Seat In' },
    { name: 'seatOut', label: 'Seat Out' },
    { name: 'crotchIn', label: 'Crotch In' },
    { name: 'crotchOut', label: 'Crotch Out' },
    { name: 'crotchLining', label: 'Crotch Lining' },
    { name: 'changeZipper', label: 'Change Zipper' },
    { name: 'lengthPants', label: 'Length' },
    { name: 'changeButtonsPants', label: 'Change Buttons' },
    { name: 'suspenderButtons', label: 'Suspender Buttons' }
];
const SEASONAL_SHIRT_KEYS = [
    { name: 'shorterSleeveShirt', label: 'Shorter Sleeve' },
    { name: 'takeInShirt', label: 'Take In' },
    { name: 'takeOutShirt', label: 'Take Out' },
    { name: 'ironShirt', label: 'Iron' }
];
const SEASONAL_VEST_KEYS = [
    { name: 'changeButtonsVest', label: 'Change Buttons' },
    { name: 'takeInVest', label: 'Take In' },
    { name: 'takeOutVest', label: 'Take Out' }
];

const initialFormData = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'User',
    position: 'Full Time',
    department: '',
    phone: '',
    active: true,
    // Seasonal rates – one per alteration (price based on job)
    ...Object.fromEntries([
        ...SEASONAL_JACKET_KEYS.map(({ name }) => [name, '']),
        ...SEASONAL_PANTS_KEYS.map(({ name }) => [name, '']),
        ...SEASONAL_SHIRT_KEYS.map(({ name }) => [name, '']),
        ...SEASONAL_VEST_KEYS.map(({ name }) => [name, ''])
    ])
};

function CreateUser() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError('Name is required');
            return false;
        }
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!formData.password) {
            setError('Password is required');
            return false;
        }
        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }
        if (!formData.position.trim()) {
            setError('Position is required');
            return false;
        }
        return true;
    };


    const createUser = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Debug: Check current user authentication
            const currentUser = auth.currentUser;
            console.log('🔐 Current user:', currentUser?.uid, currentUser?.email);
            
            if (currentUser) {
                const tokenResult = await currentUser.getIdTokenResult();
                console.log('🔐 Token claims:', tokenResult.claims);
                console.log('🔐 Is admin:', tokenResult.claims.admin);
            }

            // Get the current user's token for authentication
            const token = await currentUser.getIdToken();
            
            // Prepare roles/claims based on selected role
            const roles = {};
            if (formData.role === 'Admin') {
                roles.admin = true;
            } else {
                roles.user = true;
            }

            // Prepare user data
            const userData = {
                email: formData.email.trim().toLowerCase(),
                password: formData.password,
                name: formData.name.trim(),
                roles: roles,
                role: formData.role, // Pass the role string (Admin/User)
                position: formData.position.trim(),
                department: formData.department.trim() || null,
                phone: formData.phone.trim() || null,
                active: formData.active,
                seasonalRates: formData.position === 'Seasonal' ? (() => {
                    const allKeys = [...SEASONAL_JACKET_KEYS, ...SEASONAL_PANTS_KEYS, ...SEASONAL_SHIRT_KEYS, ...SEASONAL_VEST_KEYS];
                    return allKeys.reduce((acc, { name }) => {
                        acc[name] = formData[name] ? parseFloat(formData[name]) : null;
                        return acc;
                    }, {});
                })() : null
            };

            console.log('Creating user with data:', userData);
            
            // Try the callable function first
            let result;
            try {
                const createUserWithRole = httpsCallable(functions, 'createUserWithRole');
                result = await createUserWithRole(userData);
                console.log('User created successfully via callable:', result.data);
            } catch (callableError) {
                console.log('Callable function failed, trying HTTP approach:', callableError.message);
                
                // Fallback to HTTP request - try the HTTP function URL
                const response = await fetch('https://us-central1-jandzdashboard.cloudfunctions.net/createUserWithRoleHTTP', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(userData)
                });
                
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
                }
                
                result = await response.json();
                console.log('User created successfully via HTTP:', result);
            }
            
            console.log('User created successfully:', result.data || result);

            setSuccess(true);
            setFormData(initialFormData);

            // Redirect to user list after 2 seconds
            setTimeout(() => {
                navigate('/admin/users');
            }, 2000);

        } catch (error) {
            console.error('Error creating user:', error);
            
            // Handle Firebase Function errors
            const errorMessage = error.message || error.code || 'Unknown error';
            
            if (errorMessage.includes('email-already-in-use') || errorMessage.includes('already exists')) {
                setError('An account with this email already exists');
            } else if (errorMessage.includes('invalid-email')) {
                setError('Invalid email address');
            } else if (errorMessage.includes('weak-password') || errorMessage.includes('6 characters')) {
                setError('Password must be at least 6 characters long');
            } else if (errorMessage.includes('admin')) {
                setError('You do not have permission to create users');
            } else {
                setError(`Failed to create user: ${errorMessage}`);
            }
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="create-user-container">
                <div className="success-message">
                    <div className="success-icon">✅</div>
                    <h2>User Created Successfully!</h2>
                    <p>The new user account has been created and is now active.</p>
                    <p>Redirecting to user list...</p>
                    <Link to="/admin/users" className="back-to-users-btn">
                        Go to User List
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="create-user-container">
            <div className="create-user-header">
                <Link to="/admin/users" className="back-btn">
                    ← Back to Users
                </Link>
                <h1>Create New User</h1>
            </div>

            <div className="create-user-form-container">
                <form onSubmit={createUser} className="create-user-form">
                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="form-section">
                        <h3>Basic Information</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">Full Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter full name"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email address"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Account Security</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="password">Password *</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="Enter password (min 6 characters)"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password *</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        placeholder="Confirm password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex={-1}
                                    >
                                        {showConfirmPassword ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                                <line x1="1" y1="1" x2="23" y2="23"/>
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                <circle cx="12" cy="12" r="3"/>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Role & Position</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="role">Role *</label>
                                <select
                                    id="role"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="User">User</option>
                                    <option value="Admin">Administrator</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="position">Position *</label>
                                <select
                                    id="position"
                                    name="position"
                                    value={formData.position}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="Full Time">Full Time</option>
                                    <option value="Part Time">Part Time</option>
                                    <option value="Seasonal">Seasonal</option>
                                </select>
                            </div>
                        </div>

                        {formData.position === 'Seasonal' && (
                            <div className="seasonal-measurements-section">
                                <h4>Seasonal Measurement Rates</h4>
                                <p>Optional rate per alteration (price is based on job)</p>
                                <div className="seasonal-rates-grid">
                                    <div className="seasonal-rate-block">
                                        <h5 className="seasonal-rate-block-title">Jacket</h5>
                                        <div className="measurement-grid">
                                            {SEASONAL_JACKET_KEYS.map(({ name, label }) => (
                                                <div key={name} className="measurement-field">
                                                    <label htmlFor={name}>{label}</label>
                                                    <input
                                                        type="number"
                                                        id={name}
                                                        name={name}
                                                        value={formData[name] ?? ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Rate"
                                                        step="1"
                                                        min="0"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="seasonal-rate-block">
                                        <h5 className="seasonal-rate-block-title">Pants</h5>
                                        <div className="measurement-grid">
                                            {SEASONAL_PANTS_KEYS.map(({ name, label }) => (
                                                <div key={name} className="measurement-field">
                                                    <label htmlFor={name}>{label}</label>
                                                    <input
                                                        type="number"
                                                        id={name}
                                                        name={name}
                                                        value={formData[name] ?? ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Rate"
                                                        step="1"
                                                        min="0"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="seasonal-rate-block">
                                        <h5 className="seasonal-rate-block-title">Shirt</h5>
                                        <div className="measurement-grid">
                                            {SEASONAL_SHIRT_KEYS.map(({ name, label }) => (
                                                <div key={name} className="measurement-field">
                                                    <label htmlFor={name}>{label}</label>
                                                    <input
                                                        type="number"
                                                        id={name}
                                                        name={name}
                                                        value={formData[name] ?? ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Rate"
                                                        step="1"
                                                        min="0"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="seasonal-rate-block">
                                        <h5 className="seasonal-rate-block-title">Vest</h5>
                                        <div className="measurement-grid">
                                            {SEASONAL_VEST_KEYS.map(({ name, label }) => (
                                                <div key={name} className="measurement-field">
                                                    <label htmlFor={name}>{label}</label>
                                                    <input
                                                        type="number"
                                                        id={name}
                                                        name={name}
                                                        value={formData[name] ?? ''}
                                                        onChange={handleInputChange}
                                                        placeholder="Rate"
                                                        step="1"
                                                        min="0"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="department">Department</label>
                                <input
                                    type="text"
                                    id="department"
                                    name="department"
                                    value={formData.department}
                                    onChange={handleInputChange}
                                    placeholder="Enter department (optional)"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number (optional)"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Account Settings</h3>
                        <div className="form-row">
                            <div className="form-group checkbox-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="active"
                                        checked={formData.active}
                                        onChange={handleInputChange}
                                    />
                                    <span className="checkmark"></span>
                                    Account is active
                                </label>
                                <small>Inactive users cannot log in to the system</small>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <Link to="/admin/users" className="cancel-btn">
                            Cancel
                        </Link>
                        <button 
                            type="submit" 
                            className="create-btn"
                            disabled={loading}
                        >
                            {loading ? 'Creating User...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default CreateUser;
